import React, { useEffect, useRef, useState } from "react";
import { useLoginMutation } from "../../redux/api/authApi";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const useParticles = (canvasRef) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, frame = 0;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      speed: Math.random() * 0.00015 + 0.00005,
      opacity: Math.random() * 0.18 + 0.04,
      phase: Math.random() * Math.PI * 2,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      particles.forEach((p) => {
        const x = ((p.x + frame * p.speed) % 1) * canvas.width;
        const y = p.y * canvas.height;
        const pulse = Math.sin(frame * 0.012 + p.phase) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(188, 63, 74, ${p.opacity * pulse})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, [canvasRef]);
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [login, { isLoading, error }] = useLoginMutation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const canvasRef = useRef(null);
  useParticles(canvasRef);

  useEffect(() => {
    if (isAuthenticated) navigate("/");
    if (error) toast.error(error?.data?.message);
  }, [error, isAuthenticated, navigate]);

  const handleLogin = (e) => {
    e.preventDefault();
    login({ email, password });
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <>
      <Helmet>
        <title>Sign In — Sanguine Accent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="relative min-h-screen bg-[#f5f3ea] flex items-center justify-center px-4 overflow-hidden">

        {/* Decorative top rule */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />

        {/* Particles */}
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

        <motion.div
          className="relative z-10 max-w-md w-full space-y-6 bg-white/70 backdrop-blur-sm p-8 rounded-lg shadow-sm"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <div className="text-center space-y-1">
            
            <h1 className="text-3xl font-playfair font-bold text-[#BC3F4A]">Sign In</h1>
          </div>

          <div className="w-12 h-px bg-[#BC3F4A]/40 mx-auto" />

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full px-4 py-3 border border-[#e2e1d8] rounded-md text-[#1c1917] bg-white placeholder-stone-400 font-sourcesans focus:outline-none focus:border-[#BC3F4A] transition text-sm"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full px-4 py-3 border border-[#e2e1d8] rounded-md text-[#1c1917] bg-white placeholder-stone-400 font-sourcesans focus:outline-none focus:border-[#BC3F4A] transition text-sm"
            />

            {error && (
              <p className="text-red-600 text-sm font-sourcesans">{error.data?.message}</p>
            )}

            <div className="flex justify-end">
              <Link to="/password/forgot" className="text-xs font-sourcesans text-stone-400 hover:text-[#BC3F4A] transition">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair rounded-md transition duration-300 disabled:opacity-50"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="text-center text-xs font-sourcesans text-stone-500">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#BC3F4A] hover:text-[#a3323b] font-medium transition">
              Sign up
            </Link>
          </p>

          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sourcesans pt-2">
            Make it. Mean it.
          </p>
        </motion.div>

        {/* Decorative bottom rule */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
      </div>
    </>
  );
};

export default Login;